export interface ApiResponse<T> {
  data: T;
  message?: string;
  error?: string;
  success: boolean;
}

export class ApiClient {
  private baseUrl = import.meta.env.VITE_API_BASE_URL || '/api';
  private useMockApi = import.meta.env.VITE_USE_MOCK_API === 'true';

  async get<T>(endpoint: string, params?: Record<string, unknown>): Promise<T> {
    // In development mode without backend, throw a clear error
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && import.meta.env.DEV)) {
      throw new Error('Mock API mode: Use component-level mock data');
    }

    // Construct the full URL
    const fullUrl = `${this.baseUrl}${endpoint}`;
    const url = new URL(fullUrl, window.location.origin);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const result: ApiResponse<T> = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'API request failed');
    }

    return result.data;
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && import.meta.env.DEV)) {
      throw new Error('Mock API mode: Use component-level mock data');
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getAuthToken()}`,
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const result: ApiResponse<T> = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'API request failed');
    }

    return result.data;
  }

  async put<T>(endpoint: string, data?: unknown): Promise<T> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && import.meta.env.DEV)) {
      throw new Error('Mock API mode: Use component-level mock data');
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getAuthToken()}`,
      },
      body: data ? JSON.stringify(data) : undefined,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const result: ApiResponse<T> = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'API request failed');
    }

    return result.data;
  }

  async delete<T>(endpoint: string): Promise<T> {
    if (this.useMockApi || (!import.meta.env.VITE_API_BASE_URL && import.meta.env.DEV)) {
      throw new Error('Mock API mode: Use component-level mock data');
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.getAuthToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const result: ApiResponse<T> = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'API request failed');
    }

    return result.data;
  }

  async upload<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, unknown>,
  ): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.getAuthToken()}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const result: ApiResponse<T> = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Upload failed');
    }

    return result.data;
  }

  private getAuthToken(): string {
    // Get token from localStorage or context
    return localStorage.getItem('authToken') || '';
  }
}

export const apiClient = new ApiClient();
